import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import Loading from "../../components/loading/Loading";
import api from "../../api";
import userReportStore from "../../_store/userReportStore";

// Import Tab01 Components
import {
  ReportCountConsult,
  ReportCountConsultPaper,
  ReportCountCatheter,
  ReportCountLabelCatheter,
  ReportCountAllDepartment,
  ReportCountLabelCatheterType,
  ReportTimePaperToCatheter,
  ReportAllCountCatheter,
} from "./components/Tab01";

// Import Tab02 Components
import {
  ReportWeight,
  ReportGender,
  ReportPatientType,
  ReportHeight,
  ReportIndications,
} from "./components/Tab02";

// Import Tab03 Components
import {
  ReportTimeAttempt,
  ReportCountSucAttempt,
  ReportAttemptByDoctor,
  ReportFirstAttempt,
  ReportAverageTime,
  ReportSideCat,
  ReportConfirmation,
  ReportAttemptInfection,
} from "./components/Tab03";

// Import Tab04 Components
import {
  ReportTimeTotalDay,
  ReportReasonBystatus,
  ReportReason,
  ReportComplication,
  ReportFollowVisit,
  ReportFollowLinePatency,
} from "./components/Tab04";

// Import Rating Component
import { ReportRating } from "./components/common";

/**
 * Get user department from user_department string
 */
const getUserDepartment = (userDep) => {
  if (userDep === "super_AN" || userDep === "AN" || userDep === "NURSE") {
    return "AN";
  } else if (userDep === "special_IR" || userDep === "IR") {
    return "IR";
  } else if (userDep === "speacial_TR" || userDep === "TR") {
    return "TR";
  } else if (userDep === "special_PED" || userDep === "PED") {
    return "PED";
  }
  return "";
};

/**
 * Department selector buttons for super admin
 */
const DepartmentSelector = ({ isActivated, onChangeUserDept }) => {
  const departments = ["All", "AN", "IR", "TR", "PED"];
  
  return (
    <div className="flex gap-3 justify-center items-center mb-10 mt-20">
      {departments.map((dept) => (
        <button
          key={dept}
          className={classNames(
            "h-10 w-32 rounded-lg text-lg font-bold text-gray-700 flex justify-center items-center hover:bg-blue-500 hover:text-gray-900",
            isActivated === dept ? "bg-blue-500 text-gray-900" : "bg-blue-200"
          )}
          onClick={() => onChangeUserDept(dept)}
        >
          {dept}
        </button>
      ))}
    </div>
  );
};

/**
 * Tab navigation component
 */
const TabNavigation = ({ tab, setTab }) => {
  const tabs = [
    { id: "Tab01", label: "ขั้นตอนการรับปรึกษา" },
    { id: "Tab02", label: "Demographic ของผู้เข้ารับบริการ" },
    { id: "Tab03", label: "กระบวนการใส่สาย" },
    { id: "Tab04", label: "การติดตามผู้ป่วย" },
  ];

  const Style_tab =
    "cursor-pointer w-fit px-4 py-2 text-center text-blue-400 rounded-full hover:bg-white duration-200 h-auto text-sm";
  const Style_tab_active =
    "cursor-pointer w-fit px-4 py-2 text-center text-blue-400 rounded-full bg-white duration-200 h-auto text-sm";

  return (
    <div className="flex gap-2 justify-center mt-10 mx-auto items-center bg-bgblue100 p-2 max-w-max rounded-full">
      {tabs.map(({ id, label }) => (
        <div
          key={id}
          className={tab === id ? Style_tab_active : Style_tab}
          onClick={() => setTab(id)}
        >
          {label}
        </div>
      ))}
    </div>
  );
};

/**
 * Tab01 Content - ขั้นตอนการรับใบปรึกษา
 */
const Tab01Content = ({ userDept, reportLoading }) => {
  if (reportLoading) return <Loading />;
  
  return (
    <>
      <ReportCountConsult userDept={userDept} />
      <ReportCountConsultPaper userDept={userDept} />
    </>
  );
};

/**
 * Tab02 Content - Demographic ของผู้เข้ารับบริการ
 */
const Tab02Content = ({ userDept, reportLoading, isAN }) => {
  if (reportLoading) return <Loading />;
  
  return (
    <>
      <ReportWeight userDept={userDept} />
      <ReportGender userDept={userDept} />
      <ReportPatientType userDept={userDept} />
      <ReportAllCountCatheter userDept={userDept} />
      <ReportCountCatheter userDept={userDept} />
      <ReportCountLabelCatheter userDept={userDept} />
      {isAN && <ReportCountAllDepartment userDept={userDept} />}
      <ReportCountLabelCatheterType userDept={userDept} />
      <ReportIndications userDept={userDept} />
      <ReportTimePaperToCatheter userDept={userDept} />
      <ReportHeight userDept={userDept} />
    </>
  );
};

/**
 * Tab03 Content - กระบวนการใส่สาย
 */
const Tab03Content = ({ userDept, reportLoading }) => {
  if (reportLoading) return <Loading />;
  
  return (
    <>
      <ReportTimeAttempt userDept={userDept} />
      <ReportCountSucAttempt userDept={userDept} />
      <ReportAttemptByDoctor userDept={userDept} />
      <ReportFirstAttempt userDept={userDept} />
      <ReportAverageTime userDept={userDept} />
      <ReportSideCat userDept={userDept} />
      <ReportConfirmation userDept={userDept} />
      <ReportAttemptInfection userDept={userDept} />
    </>
  );
};

/**
 * Tab04 Content - การติดตามผู้ป่วย
 */
const Tab04Content = ({ userDept, reportLoading }) => {
  if (reportLoading) return <Loading />;
  
  return (
    <>
      <ReportTimeTotalDay userDept={userDept} />
      <ReportReasonBystatus userDept={userDept} />
      <ReportReason userDept={userDept} />
      <ReportComplication userDept={userDept} />
      <ReportFollowVisit userDept={userDept} />
      <ReportFollowLinePatency userDept={userDept} />
      <ReportRating userDept={userDept} />
    </>
  );
};

/**
 * Main Report Component
 */
const ReportMain = () => {
  const { reportLoading, setReportLoading } = userReportStore();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("");
  const { user } = useSelector((state) => state.auth);
  const [userDept, setUserDept] = useState(
    getUserDepartment(user?.user_department)
  );
  const [isActivated, setIsActivated] = useState(null);

  const isAN = user?.user_department === "super_AN";
  const isNurse =
    user?.user_department === "NURSE" ||
    user?.user_department === "AN" ||
    user?.user_department === "TR" ||
    user?.user_department === "IR" ||
    user?.user_department === "PED";

  useEffect(() => {
    if (!isActivated && (user?.user_department || userDept)) {
      setIsActivated(userDept || getUserDepartment(user?.user_department));
    }
  }, [user?.user_department, userDept, isActivated]);

  useEffect(() => {
    if (user?.user_department) {
      const checkUser = getUserDepartment(user.user_department);
      loadDashboard(checkUser);
    }
  }, [user?.user_department]);

  const loadDashboard = async (userDeptKey) => {
    try {
      await api.get("/dashboard/status", {
        params: { user_department: userDeptKey },
      });
    } catch (error) {
      console.error("Error loading dashboard:", error);
    }
  };

  // Components load their own data from backend API
  useEffect(() => {
    if (user?.user_department) {
      setReportLoading(false);
      setLoading(false);
    }
  }, [user?.user_department, userDept, setReportLoading]);

  const onChangeUserDept = (key) => {
    setUserDept(key);
    setIsActivated(key);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {isAN && (
        <DepartmentSelector
          isActivated={isActivated}
          onChangeUserDept={onChangeUserDept}
        />
      )}

      {isNurse ? (
        <div className="text-center mt-10">คุณไม่สามารถเข้าถึงหน้านี้ได้</div>
      ) : (
        <TabNavigation tab={tab} setTab={setTab} />
      )}

      <div className="max-w-3xl mx-auto">
        {tab === "Tab01" && (
          <Tab01Content userDept={userDept} reportLoading={reportLoading} />
        )}
        {tab === "Tab02" && (
          <Tab02Content
            userDept={userDept}
            reportLoading={reportLoading}
            isAN={isAN}
          />
        )}
        {tab === "Tab03" && (
          <Tab03Content userDept={userDept} reportLoading={reportLoading} />
        )}
        {tab === "Tab04" && (
          <Tab04Content userDept={userDept} reportLoading={reportLoading} />
        )}
      </div>
    </>
  );
};

export default ReportMain;
